import React from 'react';

const Section = ({ title, children }) => (
  <section className="mb-8">
    <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-3">
      {title}
    </h2>
    <div className="text-gray-700 leading-7 space-y-3">
      {children}
    </div>
  </section>
);

export default function Privacy() {
  return (
    <div className="min-h-screen bg-gray-50 py-12" data-testid="privacy-page">
      <div className="max-w-4xl mx-auto px-4">
        <article className="bg-white border border-gray-200 rounded-2xl p-6 md:p-10">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900">
            Política de Privacidad
          </h1>

          <p className="mt-3 text-gray-500">
            Última actualización: 4 de agosto de 2026
          </p>

          <p className="mt-6 text-gray-700 leading-7">
            Esta Política de Privacidad explica cómo PetAdvisor España
            recopila, utiliza, conserva y protege los datos personales de
            usuarios, proveedores y visitantes de petadvisor.es.
          </p>

          <Section title="1. Responsable del tratamiento">
            <p>
              El responsable del tratamiento es el titular de PetAdvisor
              España, con sitio web petadvisor.es.
            </p>
            <p>
              Contacto para privacidad:{' '}
              <a
                href="mailto:hola@petadvisor.es"
                className="font-semibold underline"
              >
                hola@petadvisor.es
              </a>
              .
            </p>
          </Section>

          <Section title="2. Normativa aplicable">
            <p>
              El tratamiento de datos se realiza conforme al Reglamento (UE)
              2016/679, Reglamento General de Protección de Datos (RGPD), la
              Ley Orgánica 3/2018, de Protección de Datos Personales y garantía
              de los derechos digitales (LOPDGDD), y demás normativa española
              aplicable.
            </p>
          </Section>

          <Section title="3. Datos que podemos tratar">
            <ul className="list-disc pl-6 space-y-2">
              <li>Nombre, apellidos y datos identificativos.</li>
              <li>Dirección de correo electrónico y datos de contacto.</li>
              <li>Información de la cuenta y tipo de usuario.</li>
              <li>Información profesional o comercial de proveedores.</li>
              <li>Perfiles, fotografías, reseñas y contenido publicado.</li>
              <li>Solicitudes, mensajes y comunicaciones realizadas.</li>
              <li>Información sobre planes, suscripciones y operaciones.</li>
              <li>
                Dirección IP, dispositivo, navegador, registros técnicos y
                datos de seguridad.
              </li>
              <li>
                Información derivada de cookies, cuando hayan sido aceptadas.
              </li>
            </ul>
          </Section>

          <Section title="4. Finalidades del tratamiento">
            <ul className="list-disc pl-6 space-y-2">
              <li>Crear, mantener y administrar cuentas.</li>
              <li>Publicar y gestionar perfiles de proveedores.</li>
              <li>Facilitar búsquedas, contactos y solicitudes.</li>
              <li>Publicar, moderar y gestionar reseñas.</li>
              <li>Prestar soporte y responder consultas.</li>
              <li>Gestionar planes, suscripciones y pagos.</li>
              <li>Prevenir abusos, fraude y accesos no autorizados.</li>
              <li>Mejorar el funcionamiento y la experiencia del sitio.</li>
              <li>Cumplir obligaciones legales.</li>
              <li>
                Enviar comunicaciones comerciales cuando exista consentimiento
                o una base jurídica válida.
              </li>
            </ul>
          </Section>

          <Section title="5. Bases jurídicas">
            <p>Según el servicio utilizado, las bases jurídicas pueden ser:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                La ejecución de un contrato o la aplicación de medidas
                precontractuales.
              </li>
              <li>El consentimiento otorgado por la persona interesada.</li>
              <li>El cumplimiento de obligaciones legales.</li>
              <li>
                El interés legítimo en proteger, mantener y mejorar la
                plataforma, siempre que no prevalezcan los derechos y
                libertades de la persona interesada.
              </li>
            </ul>
          </Section>

          <Section title="6. Destinatarios y proveedores tecnológicos">
            <p>
              Los datos podrán ser tratados por proveedores tecnológicos que
              prestan servicios de alojamiento, correo electrónico,
              autenticación, mapas, almacenamiento, análisis, soporte o pagos.
              Estos proveedores solo podrán tratar los datos siguiendo
              instrucciones y bajo las garantías exigidas legalmente.
            </p>
            <p>
              También podrán comunicarse datos cuando exista una obligación
              legal, requerimiento administrativo o judicial, o sea necesario
              para defender derechos legítimos.
            </p>
          </Section>

          <Section title="7. Servicios de terceros">
            <p>
              PetAdvisor puede integrar servicios como Google Maps, inicio de
              sesión mediante terceros, herramientas de correo, almacenamiento
              de imágenes o plataformas de pago. El uso de esos servicios
              puede estar sujeto a sus propias políticas de privacidad.
            </p>
          </Section>

          <Section title="8. Transferencias internacionales">
            <p>
              Algunos proveedores tecnológicos pueden tratar información fuera
              del Espacio Económico Europeo. Cuando esto ocurra, se aplicarán
              mecanismos legalmente reconocidos, como decisiones de adecuación,
              cláusulas contractuales tipo u otras garantías previstas en el
              RGPD.
            </p>
          </Section>

          <Section title="9. Conservación de los datos">
            <p>
              Los datos se conservarán durante el tiempo necesario para
              prestar el servicio y mantener la cuenta. Posteriormente podrán
              permanecer bloqueados durante los plazos necesarios para atender
              obligaciones legales, responsabilidades o reclamaciones.
            </p>
            <p>
              Los datos tratados con consentimiento se conservarán hasta que
              este sea retirado, sin afectar a la licitud del tratamiento
              realizado previamente.
            </p>
          </Section>

          <Section title="10. Derechos de las personas">
            <p>Puedes ejercer los siguientes derechos:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Acceso a tus datos personales.</li>
              <li>Rectificación de datos inexactos.</li>
              <li>Supresión de tus datos.</li>
              <li>Oposición a determinados tratamientos.</li>
              <li>Limitación del tratamiento.</li>
              <li>Portabilidad de los datos cuando corresponda.</li>
              <li>Retirada del consentimiento.</li>
              <li>
                No ser objeto de decisiones exclusivamente automatizadas cuando
                produzcan efectos jurídicos o similares significativos.
              </li>
            </ul>
            <p>
              Puedes ejercerlos escribiendo a hola@petadvisor.es. Será
              necesario aportar información suficiente para identificar la
              cuenta y verificar la identidad de la persona solicitante.
            </p>
            <p>
              También tienes derecho a presentar una reclamación ante la
              Agencia Española de Protección de Datos.
            </p>
          </Section>

          <Section title="11. Menores de edad">
            <p>
              La plataforma no está dirigida específicamente a menores. Las
              personas menores de edad deben utilizarla con la autorización y
              supervisión de sus padres, madres o representantes legales
              cuando corresponda.
            </p>
          </Section>

          <Section title="12. Cookies">
            <p>
              PetAdvisor puede utilizar cookies técnicas necesarias para
              prestar el servicio. Las cookies de análisis, publicidad o
              personalización que no sean estrictamente necesarias se
              utilizarán únicamente cuando exista una base jurídica válida y,
              cuando sea exigible, después de obtener el consentimiento.
            </p>
          </Section>

          <Section title="13. Seguridad">
            <p>
              Aplicamos medidas técnicas y organizativas razonables para
              proteger los datos frente a accesos no autorizados, pérdida,
              alteración o divulgación. Ningún sistema es completamente
              infalible, por lo que también recomendamos utilizar contraseñas
              robustas y no compartir credenciales.
            </p>
          </Section>

          <Section title="14. Cambios en esta política">
            <p>
              Esta política podrá actualizarse para reflejar cambios legales,
              técnicos o funcionales. La versión vigente estará publicada en
              esta página e indicará su fecha de actualización.
            </p>
          </Section>

          <Section title="15. Contacto">
            <p>
              Para consultas relacionadas con privacidad y protección de
              datos, escribe a{' '}
              <a
                href="mailto:hola@petadvisor.es"
                className="font-semibold underline"
              >
                hola@petadvisor.es
              </a>
              .
            </p>
          </Section>
        </article>
      </div>
    </div>
  );
}
